import emit from "../../common/events";
import { FormBaseComponent } from "../base-component/forms/form-base-component";
import { booleanConverter } from "../../common/converters";
import { capitalizeText, getUniqueId } from "../../common/utils/util";
import { css, html, nothing } from "lit";
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { property, state } from 'lit/decorators.js';
import { waitForElementsToLoad } from "../../common/utils/delay-util";

/**
 * Base select component that can be extended by more specific select components
 */
export class Select extends FormBaseComponent{

  static styles = [css`
    .ff-font{
      font-family: "ProximaNova", Helvetica;
    }
  `];

  @property()
  defaultOptionFormValue = "";

  @property()
  defaultOptionDisplayValue = "Choose an option";

  @property()
  loadingDisplayValue = "loading options"

  @property({converter: booleanConverter})
  disabled = false;

  @property({converter: booleanConverter})
  required = false;

  @property({reflect: true})
  selectedOption = '';

  @property({converter: booleanConverter})
  capitalize = false;

  @property()
  label = '';

  @state()
  protected options: object = {};

  @state()
  protected isLoading = false;

  _id = getUniqueId();

  protected async updated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(_changedProperties);
    if(_changedProperties.has("selectedOption")){
      waitForElementsToLoad(this.shadowRoot!, ['select']).then(() => {
        const select = this.shadowRoot?.querySelector("select") as HTMLSelectElement;
        select.value = this.selectedOption;
      });
    }
  }

  renderComp() {
    return html`
      <form novalidate @submit=${this.submit}>
        ${this.label ? html`<label class="form-label" for="select-${this.id}">${this.label}</label>` : nothing}
        <select name="${this.id}" id="select-${this.id}" class="form-select ff-font ${this.showValidationStying() ? 'is-invalid' : ''}"
          @change=${this.onChange} ?disabled=${this.disabled} ?required=${this.required}
          aria-describedby="select-${this.id}-field-error" aria-label="${ifDefined(this.ariaLabel)}">
          ${this.isLoading? this.showLoadingTemplate() : this.showOptionsTemplate()}
        </select>
      </form>
    `;
  }

  protected validationMessage(){
    return {
      [this.id]: this.errorMessage,
    };
  }

  private onChange(e: InputEvent){
    this.selectedOption = (e.target as HTMLSelectElement).value;
    this.onSelectOption();
  }

  protected onSelectOption() {
    throw "must be overriden"
  }

  protected showOptionsTemplate(){
    return html`<option value="${this.defaultOptionFormValue}">${this.defaultOptionDisplayValue}</option>
      ${this.options ? 
        html`${Object.entries(this.options).map( ([key,val])=> html`
          <option value="${key}" ?selected=${key === this.selectedOption}>${this.capitalize ? capitalizeText(val) : val}</option>`
        )}`
      : nothing}`;
  }

  private showLoadingTemplate(){
    return html`<option value="">${this.loadingDisplayValue}</option>`;
  }

  public getSelectedOption(){
    return this.selectedOption;
  }

  protected resetSelectOption(){
    this.selectedOption = "";
  }

  private showValidationStying(){
    return this.isDirty && !this.selectedOption
  }

  protected submit(e){
    super.submit(e);
    emit({
      element: this,
      detail: {id: this.id, selectedOption: this.selectedOption},
      name: this.FORM_SUBMIT_EVENT,
    });
  }
}
